/**
 * @file tag-list.component.tsx
 * @description Выпадающий компонент-список для выбора и управления тегами с поддержкой поиска, 
 * создания новых тегов, настроек существующих тегов и интерактивного взаимодействия через Popover.
 * Включает фильтрацию по введенному тексту и интеграцию с настройками тегов.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-03
 * @updated 2025-09-10
 * @dependencies [react, @consta/uikit/Popover, @components/ui/base/text-input, @src/types/TBaseObjectDto, @components/ui/base/text, @components/icons, axios, @utils/tag-helper, classnames]
 * @related-files [./tag-settings.component.tsx, @components/ui/base/text-input/text-input.component.tsx, @components/icons/dots-icon/dots.icon.tsx]
 */

import { useMemo, useRef, useState } from 'react';
import { Popover } from '@consta/uikit/Popover';
import { TextInput } from '@components/ui/base/text-input';
import { TBaseObjectDto } from '@src/types';
import { Text } from '@components/ui/base/text';
import { DotsIcon } from '@components/icons';
import { createTag as createTagGlobally } from '@utils/tag-helper';
import cn from 'classnames';
import { TagSettings } from './tag-settings.component';

/**
 * Props для компонента списка тегов
 */
interface ITagListProps {
    /** Обработчик скрытия списка */
    hideHandler: () => void;
    /** Обработчик изменения списка тегов */
    onChange: (newValue: TBaseObjectDto[]) => void;
    /** Обработчик клика по элементу */
    onItemClick: (item: TBaseObjectDto) => void;
    /** Ссылка на элемент привязки для Popover */
    anchorRef: React.RefObject<HTMLElement>;
    /** Массив тегов для отображения */
    items: TBaseObjectDto[];
    /** Тип тега */
    type: string;
}

export const TagList = ({ hideHandler, anchorRef, items, onChange, type, onItemClick }: ITagListProps) => {
    const [searchInput, setSearchInput] = useState<string | null>(null);
    const tagList = useMemo<TBaseObjectDto[]>(() => {
        if (!searchInput) {
            return items;
        }

        const searhString = searchInput?.toLowerCase();
        return items.filter((tag: TBaseObjectDto) => tag.$item.name.toLowerCase().indexOf(searhString) !== -1) || [];
    }, [items, searchInput]);
    const [tagSettings, setTagSettings] = useState<TBaseObjectDto | null>(null);
    const tagElementRef = useRef<HTMLDivElement | null>(null);

    const addNewTag = async (tagName: string) => {
        const newTag = { name: tagName, color: '#ff00f1', $type: type };
        const response: boolean = await createTagGlobally(newTag);

        if (!response) {
            //TODO: add error handling
            console.log('add error handling');
            return;
        }

        const newItemsList = [...(items || []), newTag];
        onChange(newItemsList);
        setSearchInput('');
    };

    return (
        <Popover
            direction="downStartRight"
            spareDirection="downStartRight"
            offset="xs"
            arrowOffset={20}
            onClickOutside={(e: Event) => {
                e.stopPropagation();
                e.preventDefault();
                hideHandler();
            }}
            isInteractive={true}
            anchorRef={anchorRef}
            equalAnchorWidth={true}
            onPointerOverCapture={() => {}}
            onPointerMoveCapture={() => {}}
        >
            <div className="bg-white rounded-sm border-solid border-[0.105vmin] border-[#00426947] flex flex-col">
                <div className={cn('px-[9px] flex justify-between w-full h-[34px] items-center hover:bg-[#F1F5F6]')}>
                    <TextInput
                        value={searchInput}
                        onChange={(value: string | null) => setSearchInput(value)}
                        placeholder="Выберите или создайте"
                        view="clear"
                        withClearButton={true}
                        autoFocus={true}
                    />
                </div>

                {tagList.map((tag: TBaseObjectDto, key: number) => (
                    <div
                        key={key}
                        className={cn(
                            'px-[9px] flex justify-between w-full h-[34px] items-center hover:bg-[#F1F5F6] cursor-pointer'
                        )}
                    >
                        <div className="flex gap-[4px] items-center w-full" onClick={() => onItemClick(tag)}>
                            <div
                                className="input_color h-[14px] w-[14px] rounded-full aspect-square"
                                style={{ background: tag.color }}
                            />

                            <Text size="s">{tag.name}</Text>
                        </div>

                        <DotsIcon
                            className="text-black hover:text-link-hover"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setTagSettings(tagSettings ? null : tag.$item);
                                tagElementRef.current = !!tagElementRef.current ? null : (e.target as HTMLDivElement);
                            }}
                        />
                    </div>
                ))}

                {searchInput && !items?.find((item: TBaseObjectDto) => item.$item.name === searchInput) && (
                    <div
                        className={cn('px-[9px] flex w-full h-[34px] items-center hover:bg-[#F1F5F6] cursor-pointer')}
                        onClick={() => addNewTag(searchInput)}
                    >
                        <Text size="s">{`Добавить тэг "${searchInput}"`}</Text>
                    </div>
                )}
            </div>

            {!!tagSettings && (
                <TagSettings
                    anchorRef={tagElementRef}
                    hideHandler={() => {
                        setTagSettings(null);
                        tagElementRef.current = null;
                    }}
                    item={tagSettings}
                    onTagChange={(changedTag: TBaseObjectDto) => {
                        const newItemList = items.map((item: TBaseObjectDto) => {
                            if (item.$item.id === changedTag.id) {
                                return { ...item, $item: changedTag, name: changedTag.name, color: changedTag.color };
                            }

                            return item;
                        });
                        onChange(newItemList);
                    }}
                    onTagRemove={(removedTag: TBaseObjectDto) => {
                        const newItemList = items.filter((item: TBaseObjectDto) => item.$item.id === removedTag.id);
                        onChange(newItemList);
                    }}
                />
            )}
        </Popover>
    );
};
